import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { LinearProgress, Tab, Tabs, Box } from '@material-ui/core';
import PropTypes from 'prop-types';

import { t, number, canURL } from '~/helpers';
import api from '~/services/api';

import GraficoArqueacaoAutomatica from '../GraficoArqueacaoAutomatica';
import TabelaArqueacaoAutomatica from '../TabelaArqueacaoAutomatica';
import TabPanelArqueacaoAutomatica from '../TabPanelArqueacaoAutomatica';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanelArqueacaoAutomatica-${index}`,
  };
}

function DialogArqueacaoAutomatica({
  data,
  setCanUpdateArqueacao,
  close,
  sending,
  onForm,
}) {
  const [tabelaFinal, setTabelaFinal] = useState([]);
  const [intervalosJaArqueados, setIntervalosJaArqueados] = useState([]);
  const [intervalosCapturados, setIntervalosCapturados] = useState([]);
  const [intervaloAceito, setIntervaloAceito] = useState({});
  const [loading, setLoading] = useState(true);
  const [tabItem, setTabItem] = useState(0);

  const { internounidatausuario } = useSelector((state) => state.user.configs);

  const cell = useMemo(
    () => [
      {
        id: 'alturahistoricoarqautitem',
        label: t('s.relatorio.descarganf.altura'),
        align: 'right',
      },
      {
        id: 'volumeoriginalhistoricoarqautitem',
        label: t('s.operacao.recebimentonf.volatual'),
        align: 'right',
        format: (value) => number(value, 2),
      },
      {
        id: 'volumehistoricoarqautitem',
        label: t('s.operacao.arqueacaoautomatica.volajustado'),
        align: 'right',
        format: (value) => (value !== null ? number(value, 2) : value),
      },
      {
        id: 'diferenca',
        label: t('s.relatorio.alteracaoencerrante.diferenca'),
        align: 'right',
        format: (value) => (value !== null ? number(value, 2) : value),
      },
    ],
    []
  );

  useEffect(() => {
    if (!data || !data.idhistoricoarqaut) {
      setTabelaFinal([]);
      setIntervalosCapturados([]);
      setIntervalosJaArqueados([]);
      setIntervaloAceito({});
      setLoading(false);

      if (setCanUpdateArqueacao) {
        setCanUpdateArqueacao(false);
      }

      return;
    }

    const fetchArqueacoes = async () => {
      try {
        setLoading(true);

        const response = await api.get(
          `historicoarqaut/${data.idhistoricoarqaut}`
        );

        const dados = response.data;

        const dtTabelaFinal = (dados.tabelafinal || []).map((item) => {
          const diferenca =
            item.volumeoriginalhistoricoarqautitem -
            item.volumehistoricoarqautitem;

          return {
            ...item,
            diferenca,
          };
        });

        setIntervalosCapturados(dados.intervaloscapturados || []);
        setIntervalosJaArqueados(dados.intervalosjaarqueados || []);
        setTabelaFinal(dtTabelaFinal);
        setIntervaloAceito(dados.intervaloconfirmado || {});

        if (setCanUpdateArqueacao) {
          const possuiIntervaloAceito = !!(
            dados.intervaloconfirmado &&
            dados.intervaloconfirmado.alturainicial !== undefined &&
            dados.intervaloconfirmado.alturafinal !== undefined &&
            dados.intervaloconfirmado.alturafinal >
              dados.intervaloconfirmado.alturainicial
          );

          const usuarioPermitido =
            (dados.aprovacaoarqautclienteconfig === 'C' &&
              canURL().aprovaarqueacao) ||
            (dados.aprovacaoarqautclienteconfig === 'U' &&
              internounidatausuario === 'S');

          setCanUpdateArqueacao(possuiIntervaloAceito && usuarioPermitido);
        }
      } catch (error) {
        console.error(error);

        setTabelaFinal([]);
        setIntervalosCapturados([]);
        setIntervalosJaArqueados([]);
        setIntervaloAceito({});

        if (setCanUpdateArqueacao) {
          setCanUpdateArqueacao(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArqueacoes();
  }, [data, setCanUpdateArqueacao, internounidatausuario]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabItem(newValue);
  }, []);

  return (
    <>
      {loading && <LinearProgress />}

      <Box style={{ borderBottom: '1px solid #3f51b5' }}>
        <Tabs
          value={tabItem}
          onChange={handleTabChange}
          aria-label="abas arqueação automática"
        >
          <Tab label={t('Gráfico')} {...a11yProps(0)} />
          <Tab label={t('Tabela')} {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanelArqueacaoAutomatica value={tabItem} index={0}>
        <GraficoArqueacaoAutomatica
          data={tabelaFinal}
          intervalosJaArqueados={intervalosJaArqueados}
          intervalosCapturados={intervalosCapturados}
          intervaloAceito={intervaloAceito}
        />
      </TabPanelArqueacaoAutomatica>

      <TabPanelArqueacaoAutomatica value={tabItem} index={1}>
        <TabelaArqueacaoAutomatica
          cells={cell}
          data={tabelaFinal}
          size="small"
          height="100%"
        />
      </TabPanelArqueacaoAutomatica>
    </>
  );
}

DialogArqueacaoAutomatica.propTypes = {
  data: PropTypes.shape({
    idhistoricoarqaut: PropTypes.number,
  }),
  setCanUpdateArqueacao: PropTypes.func,
  close: PropTypes.func,
  sending: PropTypes.bool,
  onForm: PropTypes.func,
};

DialogArqueacaoAutomatica.defaultProps = {
  data: null,
  setCanUpdateArqueacao: undefined,
  close: () => undefined,
  sending: false,
  onForm: () => undefined,
};

export default React.memo(DialogArqueacaoAutomatica);

